import { prepareInlineSvgMarkup, stripSvgNamespacePrefixes, stripSvgProlog } from './inlineSvg';
import { getPassiveTemplatePinKeys, normalizePassivePinOffsets } from './projectBuilderPassives';
import {
  BREADBOARD_PIN_KEYS,
  BREADBOARD_TEMPLATE_TYPE,
  getBreadboardBusKeyForPinKey,
} from './projectBuilderBreadboard';
import {
  SEVEN_SEGMENT_RENDER_LAYOUT,
  SEVEN_SEGMENT_SEGMENT_PIN_KEYS,
  buildSevenSegmentSegmentStates,
  getSevenSegmentComponentPinKeys,
  getSevenSegmentDigitPinKeys,
  normalizeSevenSegmentDigitCount,
  normalizeSevenSegmentSegmentStates,
} from './sevenSegmentDisplay';

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

const RESISTOR_COLORS = {
  black: '#000000',
  brown: '#8A3D06',
  red: '#C40808',
  orange: '#FF8000',
  yellow: '#FFFF00',
  green: '#008000',
  blue: '#0000FF',
  violet: '#800080',
  gray: '#808080',
  white: '#FFFFFF',
  gold: '#AD9F4E',
  silver: '#C0C0C0',
};

const PASSIVE_PREVIEW_SVG_CSS = `
  .pin-hole,
  .pin-rect {
    fill: rgba(14, 165, 233, 0.12) !important;
    stroke: rgba(2, 132, 199, 0.95) !important;
    stroke-width: 0.85px !important;
    vector-effect: non-scaling-stroke;
  }
`;

const SERVO_ACTUATOR_GROUP_ID = 'servo-actuator';
const SERVO_ACTUATOR_PIVOT = Object.freeze({
  x: 58.5,
  y: -49.5,
});
const LCD_16X2_SCREEN_GROUP_ID = 'lcd-16x2-sim-screen';
const LCD_16X2_TEMPLATE_PIN_KEYS = Object.freeze([
  'VSS', 'VDD', 'V0', 'RS', 'RW', 'E', 'D0', 'D1',
  'D2', 'D3', 'D4', 'D5', 'D6', 'D7', 'A', 'K',
]);
const LCD_16X2_TEXT_LAYOUT = Object.freeze({
  columnCenterStartX: 32.8,
  columnStepX: 10.744,
  firstLineY: 46.6,
  fontSize: 11.2,
  secondLineY: 63.8,
});
const SEVEN_SEGMENT_TEMPLATE_SEGMENT_SELECTORS = Object.freeze({
  a: '#path12',
  b: '#path11',
  c: '#path13',
  d: '#path14',
  dp: '#circle16',
  e: '#path15',
  f: '#path16',
  g: '#polygon12',
});
const SEVEN_SEGMENT_ON_COLOR = '#ff034b';
const SEVEN_SEGMENT_OFF_COLOR = '#444444';
const SEVEN_SEGMENT_BODY_FILL = '#050505';
const SEVEN_SEGMENT_BODY_STROKE = '#333333';
const SEVEN_SEGMENT_TERMINAL_FILL = '#c0c7d1';
const SEVEN_SEGMENT_TERMINAL_STROKE = '#6b7280';

const formatCoordinate = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return '0';
  }

  return String(Math.round(numericValue * 100) / 100);
};

const getResistorBands = (valueOhms) => {
  const value = Number(valueOhms);

  if (!Number.isFinite(value) || value <= 0) {
    return ['brown', 'black', 'black', 'gold'];
  }

  const digits = String(Math.round(value)).replace(/^0+/, '');
  const first = Number(digits[0] || 1);
  const second = Number(digits[1] || 0);
  const multiplier = Math.max(digits.length - 2, 0);
  const digitColors = [
    'black', 'brown', 'red', 'orange', 'yellow',
    'green', 'blue', 'violet', 'gray', 'white',
  ];

  return [
    digitColors[first],
    digitColors[second],
    digitColors[multiplier],
    'gold',
  ];
};

const parseSvgMarkup = (markup) => {
  if (typeof DOMParser === 'undefined') {
    return null;
  }

  try {
    const parsedDocument = new DOMParser().parseFromString(
      stripSvgProlog(markup).trim(),
      'image/svg+xml'
    );
    const parseError = parsedDocument.querySelector('parsererror');
    const svgRoot = parsedDocument.documentElement;

    if (parseError || !svgRoot || !svgRoot.nodeName.toLowerCase().includes('svg')) {
      return null;
    }

    return { parsedDocument, svgRoot };
  } catch {
    return null;
  }
};

const serializeSvgRoot = (svgRoot) => {
  if (!svgRoot) {
    return '';
  }

  if (typeof XMLSerializer === 'undefined') {
    return String(svgRoot.outerHTML || '').trim();
  }

  return stripSvgNamespacePrefixes(new XMLSerializer().serializeToString(svgRoot));
};

const createSvgElement = (parsedDocument, tagName, attributes = {}) => {
  const element = parsedDocument.createElementNS(SVG_NAMESPACE, tagName);

  Object.entries(attributes).forEach(([attributeName, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    element.setAttribute(attributeName, String(value));
  });

  return element;
};

const addClassToken = (element, className) => {
  const currentTokens = String(element.getAttribute('class') || '')
    .split(/\s+/)
    .map((token) => token.trim())
    .filter(Boolean);

  if (!currentTokens.includes(className)) {
    currentTokens.push(className);
    element.setAttribute('class', currentTokens.join(' '));
  }
};

const setElementFillColor = (element, fillColor) => {
  if (!element) {
    return;
  }

  element.setAttribute('fill', fillColor);
  const currentStyle = String(element.getAttribute('style') || '').trim();

  if (!currentStyle) {
    return;
  }

  if (/fill\s*:/i.test(currentStyle)) {
    element.setAttribute(
      'style',
      currentStyle.replace(/fill\s*:\s*[^;]+/i, `fill:${fillColor}`)
    );
    return;
  }

  element.setAttribute('style', `${currentStyle};fill:${fillColor}`);
};

const getNumericAttribute = (element, attributeName) => {
  const numericValue = Number(element.getAttribute(attributeName));
  return Number.isFinite(numericValue) ? numericValue : null;
};

const shiftPointList = (pointsValue, dx, dy) => {
  return String(pointsValue || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((pair) => {
      const [rawX, rawY] = pair.split(',');
      const x = Number(rawX);
      const y = Number(rawY);

      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        return pair;
      }

      return `${formatCoordinate(x + dx)},${formatCoordinate(y + dy)}`;
    })
    .join(' ');
};

const shiftPinMarkerElement = (element, dx, dy) => {
  const tagName = element.tagName?.toLowerCase();

  if (tagName === 'rect' || tagName === 'image' || tagName === 'use' || tagName === 'text') {
    const x = getNumericAttribute(element, 'x');
    const y = getNumericAttribute(element, 'y');

    if (x !== null) {
      element.setAttribute('x', formatCoordinate(x + dx));
    }

    if (y !== null) {
      element.setAttribute('y', formatCoordinate(y + dy));
    }

    if (x !== null || y !== null) {
      return;
    }
  }

  if (tagName === 'circle' || tagName === 'ellipse') {
    const cx = getNumericAttribute(element, 'cx');
    const cy = getNumericAttribute(element, 'cy');

    if (cx !== null) {
      element.setAttribute('cx', formatCoordinate(cx + dx));
    }

    if (cy !== null) {
      element.setAttribute('cy', formatCoordinate(cy + dy));
    }

    if (cx !== null || cy !== null) {
      return;
    }
  }

  if (tagName === 'line') {
    const x1 = getNumericAttribute(element, 'x1');
    const x2 = getNumericAttribute(element, 'x2');
    const y1 = getNumericAttribute(element, 'y1');
    const y2 = getNumericAttribute(element, 'y2');

    if (x1 !== null) {
      element.setAttribute('x1', formatCoordinate(x1 + dx));
    }

    if (x2 !== null) {
      element.setAttribute('x2', formatCoordinate(x2 + dx));
    }

    if (y1 !== null) {
      element.setAttribute('y1', formatCoordinate(y1 + dy));
    }

    if (y2 !== null) {
      element.setAttribute('y2', formatCoordinate(y2 + dy));
    }

    if (x1 !== null || x2 !== null || y1 !== null || y2 !== null) {
      return;
    }
  }

  if (tagName === 'polygon' || tagName === 'polyline') {
    const shiftedPoints = shiftPointList(element.getAttribute('points'), dx, dy);

    if (shiftedPoints) {
      element.setAttribute('points', shiftedPoints);
      return;
    }
  }

  const transformValue = String(element.getAttribute('transform') || '').trim();
  const translateValue = `translate(${formatCoordinate(dx)} ${formatCoordinate(dy)})`;
  element.setAttribute('transform', transformValue ? `${transformValue} ${translateValue}` : translateValue);
};

const getPinMarkerCenter = (element) => {
  const tagName = element.tagName?.toLowerCase();

  if (tagName === 'rect' || tagName === 'image' || tagName === 'use') {
    const x = getNumericAttribute(element, 'x') ?? 0;
    const y = getNumericAttribute(element, 'y') ?? 0;
    const width = getNumericAttribute(element, 'width') ?? 0;
    const height = getNumericAttribute(element, 'height') ?? 0;

    return {
      x: x + (width / 2),
      y: y + (height / 2),
    };
  }

  if (tagName === 'circle' || tagName === 'ellipse') {
    const cx = getNumericAttribute(element, 'cx');
    const cy = getNumericAttribute(element, 'cy');

    if (cx !== null && cy !== null) {
      return { x: cx, y: cy };
    }
  }

  if (tagName === 'line') {
    const x1 = getNumericAttribute(element, 'x1');
    const x2 = getNumericAttribute(element, 'x2');
    const y1 = getNumericAttribute(element, 'y1');
    const y2 = getNumericAttribute(element, 'y2');

    if (x1 !== null && x2 !== null && y1 !== null && y2 !== null) {
      return {
        x: (x1 + x2) / 2,
        y: (y1 + y2) / 2,
      };
    }
  }

  if (tagName === 'polygon' || tagName === 'polyline') {
    const points = String(element.getAttribute('points') || '')
      .trim()
      .split(/\s+/)
      .map((pair) => pair.split(',').map((part) => Number(part)))
      .filter(([x, y]) => Number.isFinite(x) && Number.isFinite(y));

    if (points.length > 0) {
      const xs = points.map(([x]) => x);
      const ys = points.map(([, y]) => y);

      return {
        x: (Math.min(...xs) + Math.max(...xs)) / 2,
        y: (Math.min(...ys) + Math.max(...ys)) / 2,
      };
    }
  }

  if (typeof element.getBBox === 'function') {
    try {
      const box = element.getBBox();

      if (
        Number.isFinite(box.x) &&
        Number.isFinite(box.y) &&
        Number.isFinite(box.width) &&
        Number.isFinite(box.height)
      ) {
        return {
          x: box.x + (box.width / 2),
          y: box.y + (box.height / 2),
        };
      }
    } catch {
      // Ignore detached-SVG bbox failures and fall through.
    }
  }

  return null;
};

const getPinMarkerKey = (element, index) => (
  element.getAttribute('data-pin') ||
  element.getAttribute('data-svgid') ||
  element.getAttribute('data-pin-number') ||
  element.getAttribute('id') ||
  `pin_${index}`
);

const collectPinMarkers = (svgRoot) => (
  Array.from(svgRoot.querySelectorAll('.pin-hole, .pin-rect'))
    .filter((element) => element.getAttribute('data-micropinouts-callout') !== '1')
    .map((element, index) => {
      const pinKey = getPinMarkerKey(element, index);
      const center = getPinMarkerCenter(element);

      if (!center) {
        return null;
      }

      return {
        pinKey,
        center,
      };
    })
    .filter(Boolean)
);

const promotePinElementToHitbox = (parsed, pinElement, pinKey) => {
  if (!parsed?.parsedDocument || !parsed?.svgRoot || !pinElement || !pinKey) {
    return;
  }

  if (pinElement.tagName.toLowerCase() !== 'rect') {
    pinElement.setAttribute('data-pin', pinKey);
    addClassToken(pinElement, 'pin-rect');
    return;
  }

  const x = Number(pinElement.getAttribute('x') || 0);
  const y = Number(pinElement.getAttribute('y') || 0);
  const width = Number(pinElement.getAttribute('width') || 0);
  const height = Number(pinElement.getAttribute('height') || 0);
  const centerX = x + (width / 2);
  const centerY = y + (height / 2);
  const hitboxWidth = Math.max(width, 8);
  const hitboxHeight = Math.max(height, 8);
  const hitbox = parsed.parsedDocument.createElementNS(SVG_NAMESPACE, 'rect');

  hitbox.setAttribute('class', 'pin-rect');
  hitbox.setAttribute('data-pin', pinKey);
  hitbox.setAttribute('x', formatCoordinate(centerX - (hitboxWidth / 2)));
  hitbox.setAttribute('y', formatCoordinate(centerY - (hitboxHeight / 2)));
  hitbox.setAttribute('width', formatCoordinate(hitboxWidth));
  hitbox.setAttribute('height', formatCoordinate(hitboxHeight));
  hitbox.setAttribute('fill', 'transparent');
  hitbox.setAttribute('stroke', 'none');

  pinElement.parentNode?.appendChild(hitbox);
};

const applyResistorBandColors = (svg, valueOhms) => {
  const [band1, band2, multiplier, tolerance] = getResistorBands(valueOhms);

  return String(svg || '')
    .replace(/id="band_1_st" fill="[^"]*"/, `id="band_1_st" fill="${RESISTOR_COLORS[band1]}"`)
    .replace(/id="band_2_nd" fill="[^"]*"/, `id="band_2_nd" fill="${RESISTOR_COLORS[band2]}"`)
    .replace(/id="band_rd_multiplier" fill="[^"]*"/, `id="band_rd_multiplier" fill="${RESISTOR_COLORS[multiplier]}"`)
    .replace(/id="gold_band" x="30.582" y="0.269" fill="[^"]*"/, `id="gold_band" x="30.582" y="0.269" fill="${RESISTOR_COLORS[tolerance]}"`);
};

const prepareGenericPassiveSvgForBuilder = (svg, pinKeys) => {
  const markup = String(svg || '').trim();

  if (!markup) {
    return '';
  }

  const parsed = parseSvgMarkup(markup);

  if (!parsed) {
    return pinKeys.reduce((currentMarkup, pinKey, index) => (
      currentMarkup.replace(
        new RegExp(`<rect([^>]*id="connector${index}pin"[^>]*)/>`, 'i'),
        `<rect$1 class="pin-rect" data-pin="${pinKey}"/>`
      )
    ), markup);
  }

  pinKeys.forEach((pinKey, index) => {
    const existingByPin = parsed.svgRoot.querySelector(`[data-pin="${pinKey}"]`);
    const connectorPin = parsed.svgRoot.querySelector(`#connector${index}pin`);
    const connectorTerminal = parsed.svgRoot.querySelector(`#connector${index}terminal`);
    const pinElement = existingByPin || connectorPin || connectorTerminal;

    if (!pinElement) {
      return;
    }

    promotePinElementToHitbox(parsed, pinElement, pinKey);
  });

  return serializeSvgRoot(parsed.svgRoot);
};

const applyLedColor = (svg, color) => (
  String(svg || '').replace(
    /(id="color_[^"]*"[^>]*fill=")[^"]*(")/g,
    `$1${color}$2`
  )
);

const prepareLedSvgForBuilder = (svg, color = '#E60000') => (
  applyLedColor(svg, color)
    .replace(/viewBox="0 0 15\.456 29\.207"/, 'viewBox="0 0 15.456 44"')
    .replace(/height="29\.207px"/, 'height="44px"')
    .replace(/y2="53\.822"/g, 'y2="44"')
    .replace(
      /<rect id="connector0pin" x="3\.451" y="25\.421" fill="none" width="2\.151" height="0\.72"\/>/,
      '<rect id="connector0pin" class="pin-rect" data-pin="K" x="3.451" y="41.5" fill="none" width="2.151" height="2.5"/>'
    )
    .replace(
      /<rect id="connector1pin" x="10\.651" y="25\.421" fill="none" width="2\.154" height="0\.72"\/>/,
      '<rect id="connector1pin" class="pin-rect" data-pin="A" x="10.651" y="41.5" fill="none" width="2.154" height="2.5"/>'
    )
);

const prepareResistorSvgForBuilder = (svg, valueOhms) => (
  applyResistorBandColors(svg, valueOhms)
    .replace(/<rect id="connector0pin"/, '<rect id="connector0pin" class="pin-rect" data-pin="A"')
    .replace(/<rect id="connector1pin"/, '<rect id="connector1pin" class="pin-rect" data-pin="B"')
);

const preparePushButtonSvgForBuilder = (svg) => (
  String(svg || '')
    .replace(/viewBox="0 0 245\.17999 245\.2"/, 'viewBox="-28 0 301 245.2"')
    .replace(
      /<g\s+id="breadboard">/,
      `<g id="breadboard">
        <rect
          id="push-button-leg-left"
          x="-24"
          y="112"
          width="28"
          height="20"
          rx="4"
          fill="#cbd5e1"
          stroke="#64748b"
          stroke-width="4"
        />
        <rect
          id="push-button-leg-right"
          x="241"
          y="112"
          width="28"
          height="20"
          rx="4"
          fill="#cbd5e1"
          stroke="#64748b"
          stroke-width="4"
        />`
    )
    .replace(
      /<rect\s+id="connector0pin"[^>]*\/>/,
      '<rect id="connector0pin" class="pin-rect" data-pin="1" x="-26" y="103" width="28" height="38" fill="none" stroke="none" stroke-width="0"/>'
    )
    .replace(
      /<rect\s+id="connector1pin"[^>]*\/>/,
      '<rect id="connector1pin" class="pin-rect" data-pin="2" x="245" y="103" width="28" height="38" fill="none" stroke="none" stroke-width="0"/>'
    )
);

const preparePotentiometerSvgForBuilder = (svg) => (
  String(svg || '')
    .replace(
      /<rect id="connector0terminal"[^>]*\/>/,
      '<rect id="connector0terminal" x="5.38" y="84.386" width="2.979" height="0.556" fill="none"/>'
    )
    .replace(
      /<rect id="connector1terminal"[^>]*\/>/,
      '<rect id="connector1terminal" x="19.78" y="84.386" width="2.979" height="0.556" fill="none"/>'
    )
    .replace(
      /<rect id="connector2terminal"[^>]*\/>/,
      '<rect id="connector2terminal" x="34.181" y="84.386" width="2.979" height="0.556" fill="none"/>'
    )
    .replace(
      /<rect id="connector0pin"[^>]*\/>/,
      `
      <rect id="pot-leg-a-visible" x="4.6" y="75.5" width="4.6" height="10.5" rx="0.6" fill="#c0c7d1" stroke="#6b7280" stroke-width="0.45"/>
      <rect id="connector0pin" class="pin-rect" data-pin="A" x="3.8" y="74.8" width="6.2" height="12" fill="transparent" stroke="none"/>
      `
    )
    .replace(
      /<rect id="connector1pin"[^>]*\/>/,
      `
      <rect id="pot-leg-w-visible" x="19.0" y="75.5" width="4.6" height="10.5" rx="0.6" fill="#c0c7d1" stroke="#6b7280" stroke-width="0.45"/>
      <rect id="connector1pin" class="pin-rect" data-pin="W" x="18.2" y="74.8" width="6.2" height="12" fill="transparent" stroke="none"/>
      `
    )
    .replace(
      /<rect id="connector2pin"[^>]*\/>/,
      `
      <rect id="pot-leg-b-visible" x="33.4" y="75.5" width="4.6" height="10.5" rx="0.6" fill="#c0c7d1" stroke="#6b7280" stroke-width="0.45"/>
      <rect id="connector2pin" class="pin-rect" data-pin="B" x="32.6" y="74.8" width="6.2" height="12" fill="transparent" stroke="none"/>
      `
    )
);

const prepareBuzzerSvgForBuilder = (svg) => (
  String(svg || '')
    .replace(
      /<rect id="connector0pin"[^>]*\/>/,
      `
      <rect id="buzzer-positive-pad-visible" x="15.3" y="26.2" width="2.9" height="2.9" rx="0.25" fill="#c0c7d1" stroke="#6b7280" stroke-width="0.35"/>
      <rect id="connector0pin" class="pin-rect" data-pin="+" x="14.4" y="25.4" width="4.8" height="4.8" fill="transparent" stroke="none"/>
      `
    )
    .replace(
      /<rect id="connector1pin"[^>]*\/>/,
      `
      <rect id="buzzer-negative-pad-visible" x="15.3" y="4.6" width="2.9" height="2.9" rx="0.25" fill="#c0c7d1" stroke="#6b7280" stroke-width="0.35"/>
      <rect id="connector1pin" class="pin-rect" data-pin="-" x="14.4" y="3.8" width="4.8" height="4.8" fill="transparent" stroke="none"/>
      `
    )
);

const prepareServoSvgForBuilder = (svg, angleDegrees = 0) => {
  const markup = String(svg || '').trim();

  if (!markup) {
    return '';
  }

  const parsed = parseSvgMarkup(markup);

  if (!parsed) {
    return String(markup)
      .replace(
        /<rect([^>]*id="connector1pin"[^>]*)\/>/i,
        '<rect$1 class="pin-rect" data-pin="SIG"/>'
      )
      .replace(
        /<rect([^>]*id="connector2pin"[^>]*)\/>/i,
        '<rect$1 class="pin-rect" data-pin="V+"/>'
      )
      .replace(
        /<rect([^>]*id="connector3pin"[^>]*)\/>/i,
        '<rect$1 class="pin-rect" data-pin="GND"/>'
      )
      .replace(
        new RegExp(`(<g[^>]*id="${SERVO_ACTUATOR_GROUP_ID}"[^>]*transform=")[^"]*(")`, 'i'),
        `$1rotate(${formatCoordinate(angleDegrees)} ${formatCoordinate(SERVO_ACTUATOR_PIVOT.x)} ${formatCoordinate(SERVO_ACTUATOR_PIVOT.y)})$2`
      );
  }

  [
    ['SIG', '#connector1pin'],
    ['V+', '#connector2pin'],
    ['GND', '#connector3pin'],
  ].forEach(([pinKey, selector]) => {
    const pinElement = parsed.svgRoot.querySelector(`[data-pin="${pinKey}"]`) ||
      parsed.svgRoot.querySelector(selector);

    if (!pinElement) {
      return;
    }

    promotePinElementToHitbox(parsed, pinElement, pinKey);
  });

  const actuatorGroup = parsed.svgRoot.querySelector(`#${SERVO_ACTUATOR_GROUP_ID}`);

  if (actuatorGroup) {
    actuatorGroup.setAttribute(
      'transform',
      `rotate(${formatCoordinate(angleDegrees)} ${formatCoordinate(SERVO_ACTUATOR_PIVOT.x)} ${formatCoordinate(SERVO_ACTUATOR_PIVOT.y)})`
    );
  }

  return serializeSvgRoot(parsed.svgRoot);
};

const createPinHitboxRect = (parsed, pinKey, center, size = 8.5) => {
  if (!parsed?.parsedDocument || !center || !pinKey) {
    return null;
  }

  const hitbox = parsed.parsedDocument.createElementNS(SVG_NAMESPACE, 'rect');
  const halfSize = size / 2;

  hitbox.setAttribute('class', 'pin-rect');
  hitbox.setAttribute('data-pin', pinKey);
  hitbox.setAttribute('x', formatCoordinate(center.x - halfSize));
  hitbox.setAttribute('y', formatCoordinate(center.y - halfSize));
  hitbox.setAttribute('width', formatCoordinate(size));
  hitbox.setAttribute('height', formatCoordinate(size));
  hitbox.setAttribute('fill', 'transparent');
  hitbox.setAttribute('stroke', 'none');

  return hitbox;
};

const prepareBreadboardSvgForBuilder = (svg) => {
  const markup = String(svg || '').trim();

  if (!markup) {
    return '';
  }

  const parsed = parseSvgMarkup(markup);

  if (!parsed) {
    return markup;
  }

  Array.from(parsed.svgRoot.querySelectorAll('.breadboard-pin-hitbox')).forEach((element) => {
    element.remove();
  });

  BREADBOARD_PIN_KEYS.forEach((pinKey) => {
    const pinGroup = parsed.svgRoot.querySelector(`#${pinKey}`);

    if (!pinGroup) {
      return;
    }

    const visibleHole = pinGroup.querySelector('circle') || pinGroup;
    const center = getPinMarkerCenter(visibleHole);
    const hitbox = createPinHitboxRect(parsed, pinKey, center, 6.8);

    if (!hitbox) {
      return;
    }

    hitbox.setAttribute('class', 'pin-rect breadboard-pin-hitbox');
    hitbox.setAttribute('data-breadboard-bus', getBreadboardBusKeyForPinKey(pinKey));
    hitbox.setAttribute('fill', 'transparent');
    hitbox.setAttribute('stroke', 'none');
    pinGroup.appendChild(hitbox);
  });

  return serializeSvgRoot(parsed.svgRoot);
};

const prepareLcd16x2SvgForBuilder = (
  svg,
  {
    textRows = [],
    isPowered = false,
    isDisplayEnabled = true,
    isBacklightOn = false,
  } = {}
) => {
  const markup = String(svg || '').trim();

  if (!markup) {
    return '';
  }

  const parsed = parseSvgMarkup(markup);
  const normalizedRows = [
    String(textRows?.[0] || '').slice(0, 16).padEnd(16, ' '),
    String(textRows?.[1] || '').slice(0, 16).padEnd(16, ' '),
  ];

  if (!parsed) {
    return prepareGenericPassiveSvgForBuilder(markup, LCD_16X2_TEMPLATE_PIN_KEYS);
  }

  LCD_16X2_TEMPLATE_PIN_KEYS.forEach((pinKey, index) => {
    const existingHitbox = parsed.svgRoot.querySelector(`[data-pin="${pinKey}"]`);

    if (existingHitbox) {
      return;
    }

    const pinElement = parsed.svgRoot.querySelector(`#connector${index}pin`);

    if (!pinElement) {
      return;
    }

    const center = getPinMarkerCenter(pinElement);
    const hitbox = createPinHitboxRect(parsed, pinKey, center);

    if (hitbox) {
      pinElement.parentNode?.appendChild(hitbox);
    } else {
      promotePinElementToHitbox(parsed, pinElement, pinKey);
    }
  });

  parsed.svgRoot.querySelector(`#${LCD_16X2_SCREEN_GROUP_ID}`)?.remove();

  const screenGroup = parsed.parsedDocument.createElementNS(SVG_NAMESPACE, 'g');
  const screenTint = parsed.parsedDocument.createElementNS(SVG_NAMESPACE, 'rect');
  const showText = Boolean(isPowered) && Boolean(isDisplayEnabled);
  const textFill = isBacklightOn ? '#102b0d' : '#1f3118';
  const textOpacity = showText ? (isBacklightOn ? '0.96' : '0.82') : '0';
  const tintOpacity = showText ? (isBacklightOn ? '0.08' : '0.04') : '0';

  screenGroup.setAttribute('id', LCD_16X2_SCREEN_GROUP_ID);

  screenTint.setAttribute('x', '18.8');
  screenTint.setAttribute('y', '27.3');
  screenTint.setAttribute('width', '188.9');
  screenTint.setAttribute('height', '47.4');
  screenTint.setAttribute('rx', '3.4');
  screenTint.setAttribute('fill', '#d6ef8c');
  screenTint.setAttribute('opacity', tintOpacity);

  screenGroup.appendChild(screenTint);

  normalizedRows.forEach((rowText, rowIndex) => {
    const baselineY = rowIndex === 0
      ? LCD_16X2_TEXT_LAYOUT.firstLineY
      : LCD_16X2_TEXT_LAYOUT.secondLineY;

    rowText.split('').forEach((character, columnIndex) => {
      if (character === ' ') {
        return;
      }

      const textElement = parsed.parsedDocument.createElementNS(SVG_NAMESPACE, 'text');

      textElement.setAttribute(
        'x',
        formatCoordinate(
          LCD_16X2_TEXT_LAYOUT.columnCenterStartX +
          (columnIndex * LCD_16X2_TEXT_LAYOUT.columnStepX)
        )
      );
      textElement.setAttribute('y', formatCoordinate(baselineY));
      textElement.setAttribute('fill', textFill);
      textElement.setAttribute('opacity', textOpacity);
      textElement.setAttribute('font-family', 'monospace');
      textElement.setAttribute('font-size', formatCoordinate(LCD_16X2_TEXT_LAYOUT.fontSize));
      textElement.setAttribute('font-weight', '700');
      textElement.setAttribute('text-anchor', 'middle');
      textElement.textContent = character;
      screenGroup.appendChild(textElement);
    });
  });

  parsed.svgRoot.appendChild(screenGroup);

  return serializeSvgRoot(parsed.svgRoot);
};

const renameDescendantIds = (rootNode, suffix) => {
  if (!rootNode) {
    return;
  }

  const elementsWithIds = [];

  if (rootNode.getAttribute?.('id')) {
    elementsWithIds.push(rootNode);
  }

  rootNode.querySelectorAll?.('[id]').forEach((element) => {
    elementsWithIds.push(element);
  });

  elementsWithIds.forEach((element) => {
    element.setAttribute('id', `${element.getAttribute('id')}-${suffix}`);
  });
};

const buildSevenSegmentDisplaySvgForBuilder = (
  svg,
  {
    digitCount = 4,
    isPowered = false,
    segmentStates = [],
  } = {}
) => {
  const markup = String(svg || '').trim();
  const activePinKeys = getSevenSegmentComponentPinKeys(digitCount);

  if (!markup) {
    return '';
  }

  const parsed = parseSvgMarkup(markup);

  if (!parsed) {
    return prepareGenericPassiveSvgForBuilder(markup, activePinKeys);
  }

  const digitTemplateGroup = parsed.svgRoot.querySelector('#g16');

  if (!digitTemplateGroup) {
    return prepareGenericPassiveSvgForBuilder(markup, activePinKeys);
  }

  const safeDigitCount = normalizeSevenSegmentDigitCount(digitCount);
  const digitPinKeys = getSevenSegmentDigitPinKeys(safeDigitCount);
  const resolvedSegmentStates = isPowered
    ? normalizeSevenSegmentSegmentStates(segmentStates, safeDigitCount, {
        fallbackMode: 'figure8',
      })
    : buildSevenSegmentSegmentStates(safeDigitCount);
  const digitStripWidth = (
    SEVEN_SEGMENT_RENDER_LAYOUT.digitWidth +
    ((safeDigitCount - 1) * SEVEN_SEGMENT_RENDER_LAYOUT.digitPitch)
  );
  const bodyWidth = Math.max(
    SEVEN_SEGMENT_RENDER_LAYOUT.minimumWidth,
    SEVEN_SEGMENT_RENDER_LAYOUT.modulePaddingX * 2 + digitStripWidth
  );
  const digitStartX = (bodyWidth - digitStripWidth) / 2;
  const topControlStripY = 0.8;
  const topControlStripHeight = 8.6;
  const bodyTopY = 10.8;
  const digitGroupOffsetY = bodyTopY - 1.2;
  const bottomControlStripY = bodyTopY + SEVEN_SEGMENT_RENDER_LAYOUT.bodyHeight + 1.5;
  const bottomControlStripHeight = Math.max(
    10,
    SEVEN_SEGMENT_RENDER_LAYOUT.totalHeight - bottomControlStripY - 1.3
  );
  const svgRoot = createSvgElement(parsed.parsedDocument, 'svg', {
    viewBox: `0 0 ${formatCoordinate(bodyWidth)} ${formatCoordinate(SEVEN_SEGMENT_RENDER_LAYOUT.totalHeight)}`,
    width: formatCoordinate(bodyWidth),
    height: formatCoordinate(SEVEN_SEGMENT_RENDER_LAYOUT.totalHeight),
    version: '1.1',
    xmlns: SVG_NAMESPACE,
  });
  const headerStrip = createSvgElement(parsed.parsedDocument, 'rect', {
    x: '5.2',
    y: formatCoordinate(topControlStripY),
    width: formatCoordinate(Math.max(20, bodyWidth - 10.4)),
    height: formatCoordinate(topControlStripHeight),
    rx: '2.2',
    fill: '#111827',
    stroke: '#1f2937',
    'stroke-width': '0.5',
  });
  const bodyRect = createSvgElement(parsed.parsedDocument, 'rect', {
    x: '0.8',
    y: formatCoordinate(bodyTopY),
    width: formatCoordinate(bodyWidth - 1.6),
    height: formatCoordinate(SEVEN_SEGMENT_RENDER_LAYOUT.bodyHeight),
    rx: '3.4',
    fill: SEVEN_SEGMENT_BODY_FILL,
    stroke: SEVEN_SEGMENT_BODY_STROKE,
    'stroke-width': '0.9',
  });
  const bezelRect = createSvgElement(parsed.parsedDocument, 'rect', {
    x: '2.2',
    y: formatCoordinate(bodyTopY + 1.5),
    width: formatCoordinate(bodyWidth - 4.4),
    height: formatCoordinate(SEVEN_SEGMENT_RENDER_LAYOUT.bodyHeight - 3),
    rx: '2.3',
    fill: 'none',
    stroke: '#111111',
    'stroke-width': '0.55',
  });
  const footerStrip = createSvgElement(parsed.parsedDocument, 'rect', {
    x: '5.2',
    y: formatCoordinate(bottomControlStripY),
    width: formatCoordinate(Math.max(20, bodyWidth - 10.4)),
    height: formatCoordinate(bottomControlStripHeight),
    rx: '2.2',
    fill: '#111827',
    stroke: '#1f2937',
    'stroke-width': '0.5',
  });

  svgRoot.appendChild(headerStrip);
  svgRoot.appendChild(bodyRect);
  svgRoot.appendChild(bezelRect);
  svgRoot.appendChild(footerStrip);

  resolvedSegmentStates.forEach((digitState, index) => {
    const digitGroup = createSvgElement(parsed.parsedDocument, 'g', {
      transform: `translate(${formatCoordinate(
        digitStartX + (index * SEVEN_SEGMENT_RENDER_LAYOUT.digitPitch)
      )} ${formatCoordinate(digitGroupOffsetY)})`,
    });
    const digitTemplateClone = digitTemplateGroup.cloneNode(true);
    const digitSuffix = `digit-${index + 1}`;

    Object.entries(SEVEN_SEGMENT_TEMPLATE_SEGMENT_SELECTORS).forEach(([segmentKey, selector]) => {
      const segmentElement = digitTemplateClone.querySelector(selector);
      const fillColor = digitState?.[segmentKey]
        ? SEVEN_SEGMENT_ON_COLOR
        : SEVEN_SEGMENT_OFF_COLOR;

      setElementFillColor(segmentElement, fillColor);
    });

    renameDescendantIds(digitTemplateClone, digitSuffix);

    digitGroup.appendChild(digitTemplateClone);
    svgRoot.appendChild(digitGroup);
  });

  const appendPinRow = ({
    pinKeys,
    centerY,
    labelY,
    padWidth = 5.6,
    padHeight = 5.8,
    hitboxWidth = 9.2,
    hitboxHeight = 9.8,
    fontSize = 4.1,
  }) => {
    const spacing = bodyWidth / (pinKeys.length + 1);

    pinKeys.forEach((pinKey, index) => {
      const centerX = spacing * (index + 1);
      const visiblePad = createSvgElement(parsed.parsedDocument, 'rect', {
        x: formatCoordinate(centerX - (padWidth / 2)),
        y: formatCoordinate(centerY - (padHeight / 2)),
        width: formatCoordinate(padWidth),
        height: formatCoordinate(padHeight),
        rx: '0.65',
        fill: SEVEN_SEGMENT_TERMINAL_FILL,
        stroke: SEVEN_SEGMENT_TERMINAL_STROKE,
        'stroke-width': '0.45',
      });
      const hitbox = createSvgElement(parsed.parsedDocument, 'rect', {
        class: 'pin-rect',
        'data-pin': pinKey,
        x: formatCoordinate(centerX - (hitboxWidth / 2)),
        y: formatCoordinate(centerY - (hitboxHeight / 2)),
        width: formatCoordinate(hitboxWidth),
        height: formatCoordinate(hitboxHeight),
        fill: 'transparent',
        stroke: 'none',
      });
      const label = createSvgElement(parsed.parsedDocument, 'text', {
        x: formatCoordinate(centerX),
        y: formatCoordinate(labelY),
        'font-family': 'monospace',
        'font-size': formatCoordinate(fontSize),
        'font-weight': '700',
        'text-anchor': 'middle',
        fill: '#cbd5e1',
      });

      label.textContent = pinKey;
      svgRoot.appendChild(visiblePad);
      svgRoot.appendChild(hitbox);
      svgRoot.appendChild(label);
    });
  };

  appendPinRow({
    pinKeys: digitPinKeys,
    centerY: topControlStripY + 2.8,
    labelY: topControlStripY + 7.1,
    padWidth: 6.3,
    padHeight: 4.9,
    hitboxWidth: 10.2,
    hitboxHeight: 8.4,
    fontSize: 3.8,
  });
  appendPinRow({
    pinKeys: SEVEN_SEGMENT_SEGMENT_PIN_KEYS,
    centerY: bottomControlStripY + 5.1,
    labelY: SEVEN_SEGMENT_RENDER_LAYOUT.totalHeight - 4.1,
    padWidth: 5.2,
    padHeight: 5.4,
    hitboxWidth: 8.4,
    hitboxHeight: 8.8,
    fontSize: 3.9,
  });

  return serializeSvgRoot(svgRoot);
};

const applyPassivePinOffsets = (markup, pinOffsets, passiveType) => {
  const parsed = parseSvgMarkup(markup);

  if (!parsed) {
    return markup;
  }

  const normalizedOffsets = normalizePassivePinOffsets(pinOffsets, passiveType);

  Array.from(parsed.svgRoot.querySelectorAll('.pin-hole, .pin-rect')).forEach((element, index) => {
    const pinKey = getPinMarkerKey(element, index);
    const offset = normalizedOffsets[pinKey];

    if (!offset) {
      return;
    }

    const dx = Number(offset.x) || 0;
    const dy = Number(offset.y) || 0;

    if (dx === 0 && dy === 0) {
      return;
    }

    shiftPinMarkerElement(element, dx, dy);
  });

  return serializeSvgRoot(parsed.svgRoot);
};

export const preparePassiveSvgBuilderMarkup = ({
  passiveType,
  svgContent,
  pinOffsets,
  ledColor = '#E60000',
  ledSvgContent = '',
  servoAngleDegrees = 0,
  servoSvgContent = '',
  sevenSegmentDigitCount = 4,
  sevenSegmentIsPowered = false,
  sevenSegmentSegmentStates = [],
  sevenSegmentSvgContent = '',
  lcdTextRows = [],
  lcdIsBacklightOn = false,
  lcdIsDisplayEnabled = true,
  lcdIsPowered = false,
  valueOhms = 10000,
} = {}) => {
  const templateType = String(passiveType || 'generic_2pin');
  const sourceSvg = String(svgContent || '').trim();
  let preparedMarkup = '';

  if (templateType === 'resistor') {
    preparedMarkup = prepareResistorSvgForBuilder(sourceSvg, valueOhms);
  } else if (templateType === 'led') {
    preparedMarkup = prepareLedSvgForBuilder(ledSvgContent || sourceSvg, ledColor);
  } else if (templateType === 'push_button') {
    preparedMarkup = preparePushButtonSvgForBuilder(sourceSvg);
  } else if (templateType === 'potentiometer') {
    preparedMarkup = preparePotentiometerSvgForBuilder(sourceSvg);
  } else if (templateType === 'buzzer') {
    preparedMarkup = prepareBuzzerSvgForBuilder(sourceSvg);
  } else if (templateType === 'servo') {
    preparedMarkup = prepareServoSvgForBuilder(servoSvgContent || sourceSvg, servoAngleDegrees);
  } else if (templateType === 'seven_segment_display') {
    preparedMarkup = buildSevenSegmentDisplaySvgForBuilder(
      sevenSegmentSvgContent || sourceSvg,
      {
        digitCount: sevenSegmentDigitCount,
        isPowered: sevenSegmentIsPowered,
        segmentStates: sevenSegmentSegmentStates,
      }
    );
  } else if (templateType === BREADBOARD_TEMPLATE_TYPE) {
    preparedMarkup = prepareBreadboardSvgForBuilder(sourceSvg);
  } else if (templateType === 'lcd_16x2') {
    preparedMarkup = prepareLcd16x2SvgForBuilder(sourceSvg, {
      textRows: lcdTextRows,
      isBacklightOn: lcdIsBacklightOn,
      isDisplayEnabled: lcdIsDisplayEnabled,
      isPowered: lcdIsPowered,
    });
  } else {
    preparedMarkup = prepareGenericPassiveSvgForBuilder(
      sourceSvg,
      getPassiveTemplatePinKeys(templateType)
    );
  }

  if (!preparedMarkup) {
    return '';
  }

  return applyPassivePinOffsets(preparedMarkup, pinOffsets, templateType);
};

export const buildPassivePreviewSvgData = ({
  passiveType,
  svgContent,
  pinOffsets,
  ledColor = '#E60000',
  ledSvgContent = '',
  servoAngleDegrees = 0,
  servoSvgContent = '',
  sevenSegmentDigitCount = 4,
  sevenSegmentIsPowered = false,
  sevenSegmentSegmentStates = [],
  sevenSegmentSvgContent = '',
  lcdTextRows = [],
  lcdIsBacklightOn = false,
  lcdIsDisplayEnabled = true,
  lcdIsPowered = false,
  valueOhms = 10000,
} = {}) => {
  const preparedMarkup = preparePassiveSvgBuilderMarkup({
    passiveType,
    svgContent,
    pinOffsets,
    ledColor,
    ledSvgContent,
    servoAngleDegrees,
    servoSvgContent,
    sevenSegmentDigitCount,
    sevenSegmentIsPowered,
    sevenSegmentSegmentStates,
    sevenSegmentSvgContent,
    lcdTextRows,
    lcdIsBacklightOn,
    lcdIsDisplayEnabled,
    lcdIsPowered,
    valueOhms,
  });

  if (!preparedMarkup) {
    return {
      markup: '',
      pins: [],
    };
  }

  const parsed = parseSvgMarkup(preparedMarkup);

  if (!parsed) {
    return {
      markup: prepareInlineSvgMarkup(preparedMarkup, { extraCssText: PASSIVE_PREVIEW_SVG_CSS }),
      pins: [],
    };
  }

  const pins = collectPinMarkers(parsed.svgRoot);

  return {
    markup: prepareInlineSvgMarkup(
      serializeSvgRoot(parsed.svgRoot),
      { extraCssText: PASSIVE_PREVIEW_SVG_CSS }
    ),
    pins,
  };
};
